import { z } from "zod";
import {
  ProfileCompanyTypes,
  ProfileEmployeePoolSize,
  ProfileExperienceLevelEnum,
  ProfileGenderEnum,
  ProfileLanguageProficiency,
  ProfileTemplatesEnum,
} from "./profile-statics";
import { zLocationParameter } from "../users/user-parameters";
import { zDate, zObjectId } from "../../utils";

const zCommonProfileParameters = {
  selectedProfileTemplate: z.nativeEnum(ProfileTemplatesEnum).optional(),

  isOnboardingComplete: z.boolean().optional(),
  primaryLocation: z.object(zLocationParameter).optional(),

  languages: z
    .object({
      languageName: z.string().max(80),
      proficiency: z.nativeEnum(ProfileLanguageProficiency),
    })
    .array()
    .max(5)
    .optional(),
};

const zProfileCompanyInfoParameters = {
  legalName: z.string().max(40),
  dba: z.string().max(40).optional(),
  about: z.string().max(250),
};

const zProfileCompanyOverviewParameters = {
  companyType: z.nativeEnum(ProfileCompanyTypes),
  numberOfEmployees: z.nativeEnum(ProfileEmployeePoolSize),
};

const zProfileCommonProviderParameters = {
  experienceLevel: z.nativeEnum(ProfileExperienceLevelEnum).optional(),
  linkedAccounts: z
    .object({
      accountOn: z.string().max(15),
      profileLink: z.string().url().max(50),
    })
    .array()
    .max(5)
    .optional(),
  profileCategory: z
    .object({
      id: zObjectId(),
      subCategoryId: zObjectId().optional(),
      skillsIds: z.string().array().max(30),
    })
    .optional(),
};

export const zClientProfileParameters = z.object({
  ...zCommonProfileParameters,
});

export const zCompanyProfileParameters = z.object({
  ...zCommonProfileParameters,

  availableOn: z
    .object({ name: z.string().max(40) })
    .array()
    .max(8)
    .optional(),

  companyInfo: z.object({ ...zProfileCompanyInfoParameters }).optional(),
  companyOverview: z
    .object({
      ...zProfileCompanyOverviewParameters,
      representativeTitle: z.string().max(40),
    })
    .optional(),
});

export const zProfessionalProfileParameters = z.object({
  ...zCommonProfileParameters,
  ...zProfileCommonProviderParameters,
  title: z.string().max(40).optional(),
  overview: z.string().max(250).optional(),

  cvLink: z.string().url().max(150).optional(),
  workHistory: z
    .object({
      company: z.string().max(40),
      jobTitle: z.string().max(40),
      startAt: zDate(),
      endAt: zDate().optional(),
      isWorking: z.boolean().optional(),
      description: z.string().optional(),
      location: z.object({
        city: z.string().max(50),
        country: z.string().max(50),
      }),
    })
    .array()
    .max(5)
    .optional(),
  educationHistory: z
    .object({
      college: z.string().max(80),
      area: z.string().max(80).optional(),
      degree: z.string().max(80).optional(),
      startAt: zDate().optional(),
      endAt: zDate().optional(),
      currentlyStudying: z.boolean().optional(),
      description: z.string().max(250).optional(),
    })
    .array()
    .max(5)
    .optional(),
});

export const zBusinessProfileParameters = z.object({
  ...zCommonProfileParameters,
  ...zProfileCommonProviderParameters,
  secondaryLocation: z.object(zLocationParameter).optional(),
  trustedBy: z
    .object({ name: z.string().max(40) })
    .array()
    .max(8)
    .optional(),

  companyInfo: z
    .object({
      ...zProfileCompanyInfoParameters,
      licenseNumber: z.string().max(40),
    })
    .optional(),

  companyOverview: z
    .object({
      ...zProfileCompanyOverviewParameters,
      industryId: zObjectId(),
      establishedYear: zDate(),
    })
    .optional(),

  representativeInfo: z
    .object({
      firstName: z.string().max(40),
      lastName: z.string().max(40),
      title: z.string().max(40),
      gender: z.nativeEnum(ProfileGenderEnum),
      dob: zDate(),
      country: z.string().max(50),
      phoneNumber: z.string().max(15),
    })
    .optional(),
  qualifications: z
    .object({
      title: z.string().max(40),
      issuedAt: zDate(),
      expiresAt: zDate().optional(),
      certificateLink: z.string().url().max(150),
    })
    .array()
    .max(5)
    .optional(),
});

export type ClientProfileParameters = z.infer<typeof zClientProfileParameters>;
export type CompanyProfileParameters = z.infer<typeof zCompanyProfileParameters>;
export type ProfessionalProfileParameters = z.infer<typeof zProfessionalProfileParameters>;
export type BusinessProfileParameters = z.infer<typeof zBusinessProfileParameters>;
